import { observer } from 'mobx-react-lite'
import { track } from '@tldraw/tldraw'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { useRelevantStyles } from 'components/VisualStudioPlayground/VisualEditor/hooks/useRelevantStyles'
import { FontFamilyStyle, FontWeightStyle } from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import Styles from './FontFamilyPanel.module.scss'
import { FontFamilyListBox } from './FontFamilyListBox'
import { useUpdateStyle } from 'components/VisualStudioPlayground/VisualEditor/hooks/useUpdateStyle'
import { Input } from 'almanac-ui/TextInputField/Input'
import { useState } from 'react'
import { Search } from 'blaze-ui/icons'
import { Key } from 'react'

interface FontFamilyPanelBaseProps {
  selectedFont: string
  filter: string
  onFilterChange: (filter: string) => void
  onSelectionChange: (key: Key, weights: (100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900)[]) => void
  inline?: boolean
}

export const FontFamilyPanelBase = observer<FontFamilyPanelBaseProps>(
  ({ selectedFont, filter, onFilterChange, onSelectionChange, inline }) => {
    return (
      <div className={Styles.container}>
        <div className={Styles.searchWrapper}>
          <Search />
          <Input
            name="Font filter"
            placeholder="Search fonts..."
            size="sm"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            className={Styles.searchInput}
            withBorder={false}
            autoComplete="off"
          />
        </div>
        <FontFamilyListBox
          selectedFont={selectedFont}
          filter={filter}
          onSelectionChange={onSelectionChange}
          inline={inline}
        />
      </div>
    )
  },
)

export const FontFamilyPanel = track(
  observer(({ inline = false }: { inline?: boolean }) => {
    const editor = useEditor()
    const relevantStyles = useRelevantStyles(editor)
    const updateStyle = useUpdateStyle(editor)
    const [filter, setFilter] = useState('')

    if (!editor || !relevantStyles) return null

    const { styles } = relevantStyles

    const fontFamily = styles.get(FontFamilyStyle)
    const fontWeight = styles.get(FontWeightStyle)

    if (!fontFamily || !fontWeight) return null

    const selectedFont = fontFamily.type === 'shared' ? fontFamily.value : 'Mixed'

    return (
      <FontFamilyPanelBase
        inline={inline}
        selectedFont={selectedFont}
        filter={filter}
        onFilterChange={setFilter}
        onSelectionChange={(key, weights) => {
          if (!key) return

          updateStyle(FontFamilyStyle, key as string)

          // If we cannot maintain the current font weight, reset it to 400
          if (fontWeight.type === 'mixed' || (fontWeight.type === 'shared' && !weights.includes(fontWeight.value))) {
            updateStyle(FontWeightStyle, 400)
          }
        }}
      />
    )
  }),
)
